import { createPortal } from "react-dom";
import { useModalAccessibility } from "@/lib/useModalAccessibility";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Check,
  Database,
  Download,
  History,
  Loader2,
  Maximize2,
  Minimize2,
  Plus,
  Send,
  Trash2,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/Button";
import { Markdown } from "@/components/library/Markdown";
import {
  confirmAssistantAction,
  requestConversationTitle,
  streamAssistantChat,
  useArchiveConversation,
  useAssistantConversations,
  type AssistantPendingAction,
  type AssistantStreamEvent,
} from "@/lib/data/assistant";
import { supabase } from "@/lib/supabase";
import { NoaIcon } from "./NoaIcon";
import { suggestionsForRoute } from "./suggestions";
import { IaMention } from "@/components/legal/IaMention";

// =============================================================================
// AssistantPanel — panneau de chat de l'agent IA « Noa ».
// =============================================================================
// Streaming SSE, activité des tools, cartes de confirmation d'action,
// suggestions contextuelles selon la page, historique des conversations.
// =============================================================================

interface ToolActivity {
  name: string;
  status: "running" | "done" | "error";
  error?: string;
}

interface ChatItem {
  id: string;
  role: "user" | "assistant";
  content: string;
  tools: ToolActivity[];
  pendingAction?: AssistantPendingAction | null;
  streaming?: boolean;
  error?: string | null;
}

interface AssistantPanelProps {
  open: boolean;
  onClose: () => void;
  route: string;
  initialDraft?: string;
  onDraftConsumed?: () => void;
}

let itemSeq = 0;
const nextId = () => `local-${++itemSeq}`;

export function AssistantPanel({
  open,
  onClose,
  route,
  initialDraft,
  onDraftConsumed,
}: AssistantPanelProps) {
  const { t } = useTranslation();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [items, setItems] = useState<ChatItem[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  useModalAccessibility(modalRef, open, onClose);

  const conversations = useAssistantConversations();
  const archive = useArchiveConversation();

  // Pré-remplissage du champ (openWithPrompt)
  useEffect(() => {
    if (open && initialDraft) {
      setInput(initialDraft);
      onDraftConsumed?.();
    }
  }, [open, initialDraft, onDraftConsumed]);

  // Focus + ESC
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => inputRef.current?.focus(), 150);
    return () => {
      clearTimeout(timer);
    };
  }, [open, onClose]);

  // Autoscroll
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [items]);

  const updateItem = useCallback(
    (id: string, patch: Partial<ChatItem> | ((prev: ChatItem) => ChatItem)) => {
      setItems((prev) =>
        prev.map((it) =>
          it.id === id ? (typeof patch === "function" ? patch(it) : { ...it, ...patch }) : it,
        ),
      );
    },
    [],
  );

  const send = useCallback(
    async (text: string) => {
      const message = text.trim();
      if (!message || busy) return;
      setInput("");
      setBusy(true);

      const userItem: ChatItem = { id: nextId(), role: "user", content: message, tools: [] };
      const assistantId = nextId();
      const assistantItem: ChatItem = {
        id: assistantId,
        role: "assistant",
        content: "",
        tools: [],
        streaming: true,
      };
      setItems((prev) => [...prev, userItem, assistantItem]);

      const controller = new AbortController();
      abortRef.current = controller;
      let convId = conversationId;
      const isFirstMessage = !conversationId;

      try {
        await streamAssistantChat({
          conversationId,
          message,
          pageContext: { route },
          signal: controller.signal,
          onEvent: (event: AssistantStreamEvent) => {
            if (event.type === "conversation") {
              convId = event.conversationId;
              setConversationId(event.conversationId);
            } else if (event.type === "delta") {
              updateItem(assistantId, (prev) => ({ ...prev, content: prev.content + event.text }));
            } else if (event.type === "final") {
              // Texte définitif nettoyé côté serveur (liens sandbox retirés...).
              updateItem(assistantId, { content: event.text });
            } else if (event.type === "tool") {
              updateItem(assistantId, (prev) => {
                const tools = [...prev.tools];
                const idx = tools.findIndex(
                  (tl) => tl.name === event.name && tl.status === "running",
                );
                if (event.status === "running" && idx < 0) {
                  tools.push({ name: event.name, status: "running" });
                } else if (idx >= 0) {
                  tools[idx] = { name: event.name, status: event.status, error: event.error };
                }
                return { ...prev, tools };
              });
            } else if (event.type === "pending_action") {
              updateItem(assistantId, { pendingAction: event.action });
            } else if (event.type === "error") {
              updateItem(assistantId, { error: event.error, streaming: false });
            }
          },
        });
      } catch (e) {
        updateItem(assistantId, {
          error: e instanceof Error ? e.message : t("ui.assistant.error_generic"),
        });
      } finally {
        updateItem(assistantId, { streaming: false });
        setBusy(false);
        abortRef.current = null;
        if (isFirstMessage && convId) void requestConversationTitle(convId);
      }
    },
    [busy, conversationId, route, t, updateItem],
  );

  const handleConfirm = useCallback(
    async (item: ChatItem, action: AssistantPendingAction, approve: boolean) => {
      if (!conversationId) return;
      updateItem(item.id, (prev) => ({
        ...prev,
        pendingAction: prev.pendingAction ? { ...prev.pendingAction, status: "confirmed" } : null,
      }));
      setBusy(true);
      try {
        const res = await confirmAssistantAction({ conversationId, actionId: action.id, approve });
        updateItem(item.id, (prev) => ({
          ...prev,
          pendingAction: prev.pendingAction
            ? {
                ...prev.pendingAction,
                status: approve ? (res.error ? "failed" : "executed") : "rejected",
                result: res.result,
              }
            : null,
        }));
        if (approve && res.followUp) {
          const followItem: ChatItem = {
            id: nextId(),
            role: "assistant",
            content: res.followUp,
            tools: [],
          };
          setItems((prev) => [...prev, followItem]);
        }
      } catch (e) {
        updateItem(item.id, (prev) => ({
          ...prev,
          pendingAction: prev.pendingAction ? { ...prev.pendingAction, status: "pending" } : null,
          error: e instanceof Error ? e.message : t("ui.assistant.error_generic"),
        }));
      } finally {
        setBusy(false);
      }
    },
    [conversationId, t, updateItem],
  );

  const startNewConversation = useCallback(() => {
    abortRef.current?.abort();
    setConversationId(null);
    setItems([]);
    setShowHistory(false);
    setInput("");
  }, []);

  const openConversation = useCallback(async (id: string) => {
    setShowHistory(false);
    setConversationId(id);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data } = await (supabase as any)
      .from("assistant_messages")
      .select("id, role, content, tool_calls, created_at")
      .eq("conversation_id", id)
      .order("created_at", { ascending: true })
      .limit(200);
    const loaded: ChatItem[] = (
      (data ?? []) as {
        id: string;
        role: string;
        content: string | null;
        tool_calls: ToolActivity[] | null;
      }[]
    )
      .filter((m) => m.role === "user" || m.role === "assistant")
      .map((m) => ({
        id: m.id,
        role: m.role as "user" | "assistant",
        content: m.content ?? "",
        tools: (m.tool_calls ?? []).map((tc) => ({ name: tc.name, status: "done" as const })),
      }));
    setItems(loaded);
  }, []);

  const suggestions = suggestionsForRoute(route);

  if (!open) return null;
  return createPortal(
    <div
      ref={modalRef}
      tabIndex={-1}
      aria-label={t("ui.assistant.title")}
      className={cn(
        "fixed inset-0 z-50 !mt-0",
        open ? "pointer-events-auto" : "pointer-events-none",
      )}
      aria-hidden={!open}
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className={cn(
          "absolute inset-0 bg-bg-inverse/40 transition-opacity duration-200",
          open ? "opacity-100" : "opacity-0",
        )}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={cn(
          "absolute inset-y-0 right-0 flex w-full transform flex-col bg-surface shadow-2xl transition-all duration-200 ease-out",
          fullscreen ? "sm:max-w-full" : "sm:max-w-lg",
          open ? "translate-x-0" : "translate-x-full",
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="grid size-9 place-items-center rounded-full bg-brand text-brand-foreground">
              <NoaIcon className="size-5" />
            </span>
            <div>
              <p className="text-sm font-semibold text-ink">{t("ui.assistant.title")}</p>
              <p className="text-xs text-ink-muted">{t("ui.assistant.subtitle")}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setFullscreen((v) => !v)}
              className="focus-ring hidden size-9 place-items-center rounded-lg text-ink-muted hover:bg-bg-subtle sm:grid"
              aria-label={t(
                fullscreen ? "ui.assistant.exit_fullscreen" : "ui.assistant.fullscreen",
              )}
              title={t(fullscreen ? "ui.assistant.exit_fullscreen" : "ui.assistant.fullscreen")}
            >
              {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            <button
              type="button"
              onClick={startNewConversation}
              className="focus-ring grid size-9 place-items-center rounded-lg text-ink-muted hover:bg-bg-subtle"
              aria-label={t("ui.assistant.new_conversation")}
              title={t("ui.assistant.new_conversation")}
            >
              <Plus className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setShowHistory((v) => !v)}
              className={cn(
                "focus-ring grid size-9 place-items-center rounded-lg hover:bg-bg-subtle",
                showHistory ? "text-brand" : "text-ink-muted",
              )}
              aria-label={t("ui.assistant.history")}
              title={t("ui.assistant.history")}
            >
              <History className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="focus-ring grid size-9 place-items-center rounded-lg text-ink-muted hover:bg-bg-subtle"
              aria-label={t("common.close")}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Historique */}
        {showHistory ? (
          <div
            className={cn("flex-1 overflow-y-auto p-3", fullscreen && "mx-auto w-full max-w-3xl")}
          >
            {(conversations.data ?? []).length === 0 ? (
              <p className="px-2 py-8 text-center text-sm text-ink-muted">
                {t("ui.assistant.no_history")}
              </p>
            ) : (
              <ul className="space-y-1">
                {(conversations.data ?? []).map((conv) => (
                  <li key={conv.id} className="group flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => void openConversation(conv.id)}
                      className={cn(
                        "focus-ring flex-1 truncate rounded-lg px-3 py-2.5 text-left text-sm hover:bg-bg-subtle",
                        conv.id === conversationId
                          ? "bg-brand-muted font-medium text-brand"
                          : "text-ink",
                      )}
                    >
                      {conv.title ?? t("ui.assistant.untitled")}
                    </button>
                    <button
                      type="button"
                      onClick={() => archive.mutate(conv.id)}
                      className="focus-ring grid size-8 shrink-0 place-items-center rounded-lg text-ink-muted opacity-0 hover:bg-bg-subtle hover:text-danger group-hover:opacity-100"
                      aria-label={t("common.delete")}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <>
            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
              <div className={cn("space-y-4", fullscreen && "mx-auto h-full w-full max-w-3xl")}>
                {items.length === 0 ? (
                  <EmptyChat suggestions={suggestions} onPick={(text) => void send(text)} />
                ) : (
                  items.map((item) => (
                    <MessageBubble
                      key={item.id}
                      item={item}
                      onConfirm={handleConfirm}
                      busy={busy}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Suggestions au fil de l'eau */}
            {items.length > 0 && !busy && suggestions.length > 0 ? (
              <div
                className={cn(
                  "flex gap-2 overflow-x-auto px-4 pb-2",
                  fullscreen && "mx-auto w-full max-w-3xl",
                )}
              >
                {suggestions.slice(0, 3).map((key) => (
                  <SuggestionChip key={key} labelKey={key} onPick={(text) => void send(text)} />
                ))}
              </div>
            ) : null}

            {/* Composer */}
            <div className="border-t border-line p-3">
              <div className={cn(fullscreen && "mx-auto w-full max-w-3xl")}>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void send(input);
                  }}
                  className="flex items-end gap-2"
                >
                  <textarea
                    ref={inputRef}
                    aria-label={t("ui.assistant.placeholder")}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        void send(input);
                      }
                    }}
                    rows={Math.min(4, Math.max(1, input.split("\n").length))}
                    placeholder={t("ui.assistant.placeholder")}
                    className="focus-ring max-h-32 flex-1 resize-none rounded-xl border border-line bg-bg-subtle px-3.5 py-2.5 text-sm text-ink placeholder:text-ink-muted"
                  />
                  <Button
                    type="submit"
                    aria-label="Envoyer la demande à l’assistant"
                    size="sm"
                    disabled={busy || !input.trim()}
                    className="h-10 w-10 rounded-xl px-0"
                  >
                    {busy ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </form>
                <p className="mt-1.5 px-1 text-[11px] text-ink-muted">
                  {t("ui.assistant.disclaimer")}
                </p>
                {/* Mention courte IA (A3), administrable, lien vers la page Confidentialité. */}
                <IaMention className="mt-1 flex items-start gap-1 px-1 text-[11px] text-ink-subtle" />
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

// -----------------------------------------------------------------------------
// Sous-composants
// -----------------------------------------------------------------------------
function EmptyChat({
  suggestions,
  onPick,
}: {
  suggestions: string[];
  onPick: (text: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col items-center justify-center gap-5 px-4 text-center">
      <span className="grid size-14 place-items-center rounded-2xl bg-brand-muted text-brand">
        <NoaIcon className="size-7" />
      </span>
      <div>
        <p className="text-base font-semibold text-ink">{t("ui.assistant.empty_title")}</p>
        <p className="mt-1 text-sm text-ink-muted">{t("ui.assistant.empty_subtitle")}</p>
      </div>
      {suggestions.length > 0 ? (
        <div className="flex w-full flex-col gap-2">
          {suggestions.map((key) => (
            <SuggestionChip key={key} labelKey={key} onPick={onPick} block />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function SuggestionChip({
  labelKey,
  onPick,
  block,
}: {
  labelKey: string;
  onPick: (text: string) => void;
  block?: boolean;
}) {
  const { t } = useTranslation();
  const label = t(`ui.assistant.sugg.${labelKey}`);
  return (
    <button
      type="button"
      onClick={() => onPick(label)}
      className={cn(
        "focus-ring rounded-xl border border-line bg-surface px-3.5 py-2 text-left text-sm text-ink transition-colors hover:border-brand/40 hover:bg-brand-muted",
        block ? "w-full" : "shrink-0 whitespace-nowrap",
      )}
    >
      {label}
    </button>
  );
}

function MessageBubble({
  item,
  onConfirm,
  busy,
}: {
  item: ChatItem;
  onConfirm: (item: ChatItem, action: AssistantPendingAction, approve: boolean) => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  if (item.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-brand px-4 py-2.5 text-sm text-brand-foreground">
          {item.content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {/* Activité des tools */}
      {item.tools.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {item.tools.map((tool, i) => (
            <span
              key={`${tool.name}-${i}`}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium",
                tool.status === "error"
                  ? "border-danger/30 bg-danger/5 text-danger"
                  : "border-line bg-bg-subtle text-ink-muted",
              )}
            >
              {tool.status === "running" ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : tool.status === "error" ? (
                <AlertTriangle className="h-3 w-3" />
              ) : tool.name === "export_data" ? (
                <Download className="h-3 w-3" />
              ) : (
                <Database className="h-3 w-3" />
              )}
              {t(`ui.assistant.tools.${tool.name}`, { defaultValue: tool.name })}
            </span>
          ))}
        </div>
      ) : null}

      {item.content ? (
        <div className="max-w-[95%] rounded-2xl rounded-bl-md border border-line bg-surface px-4 py-3 text-sm text-ink shadow-sm [&_h3]:mt-2 [&_h3]:text-sm [&_h3]:font-semibold">
          <Markdown source={item.content} />
        </div>
      ) : item.streaming ? (
        <div className="flex items-center gap-2 px-1 text-sm text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("ui.assistant.thinking")}
        </div>
      ) : null}

      {item.error ? (
        <div className="flex items-start gap-2 rounded-xl border border-danger/30 bg-danger/5 px-3.5 py-2.5 text-sm text-danger">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          {item.error}
        </div>
      ) : null}

      {item.pendingAction ? (
        <PendingActionCard
          item={item}
          action={item.pendingAction}
          onConfirm={onConfirm}
          busy={busy}
        />
      ) : null}
    </div>
  );
}

function PendingActionCard({
  item,
  action,
  onConfirm,
  busy,
}: {
  item: ChatItem;
  action: AssistantPendingAction;
  onConfirm: (item: ChatItem, action: AssistantPendingAction, approve: boolean) => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const clientAction =
    (action.result as { client_action?: string; dossier_id?: string } | null) ?? null;

  return (
    <div className="rounded-xl border border-brand/30 bg-brand-muted/50 p-3.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-brand">
        {t("ui.assistant.action_title")}
      </p>
      <p className="mt-1 text-sm font-medium text-ink">{action.summary ?? action.tool_name}</p>

      {action.status === "pending" ? (
        <div className="mt-3 flex gap-2">
          <Button
            size="sm"
            onClick={() => onConfirm(item, action, true)}
            disabled={busy}
            iconLeft={<Check className="h-4 w-4" />}
          >
            {t("common.confirm")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onConfirm(item, action, false)}
            disabled={busy}
          >
            {t("common.cancel")}
          </Button>
        </div>
      ) : action.status === "confirmed" ? (
        <p className="mt-2 flex items-center gap-1.5 text-sm text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> {t("ui.assistant.action_running")}
        </p>
      ) : action.status === "executed" ? (
        <div className="mt-2">
          <p className="flex items-center gap-1.5 text-sm font-medium text-success">
            <Check className="h-4 w-4" /> {t("ui.assistant.action_done")}
          </p>
          {clientAction?.client_action === "generate_bri" && clientAction.dossier_id ? (
            <Link
              to={`/app/aon/dossiers/${clientAction.dossier_id}`}
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:underline"
            >
              <Download className="h-4 w-4" /> {t("ui.assistant.open_dossier_bri")}
            </Link>
          ) : null}
        </div>
      ) : action.status === "failed" ? (
        <p className="mt-2 flex items-center gap-1.5 text-sm text-danger">
          <AlertTriangle className="h-4 w-4" /> {t("ui.assistant.action_failed")}
        </p>
      ) : (
        <p className="mt-2 text-sm text-ink-muted">{t("ui.assistant.action_rejected")}</p>
      )}
    </div>
  );
}
